/**
 * useNotesSession — the player side of the content review, and what the chat
 * is about to send.
 *
 * The right-hand column is the shared `ChatThread`; it owns the text, replies,
 * edits and the optimistic bubble. This hook owns what the chat cannot know:
 * the player transport (seek, frame step, full view, scrubber markers), the
 * capture → markup flow, the **staged moment** — a pinned timestamp or a
 * captured frame waiting to ride the next send — and the **staged files**
 * (`richComposer` on the chat). Each staged file and frame uploads as soon as
 * it is attached, so Send only posts the message; whatever is still in
 * transfer, or failed to upload, is finished by the send.
 *
 * The panel calls this unconditionally — `enabled: false` must be completely
 * inert (no shortcuts, no listeners, no fetches).
 */
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type RefObject,
} from 'react';
import { toast } from 'sonner';

import {
  deleteThreadFilesOnExit,
  MessageSendError,
  useAnchoredMessages,
  useSendMessage,
  useThreadFilePolicy,
  type EncodedImage,
  type FrameDraft,
  type SendFailureKind,
} from '@discovr/features';
import { threadFileAccept, type ReviewThreadEntry, type ThreadFilePolicy } from '@discovr/types';

import { useWindowEvent } from '../../../../../lib/browser/useEventListener';
import { formatBytes } from '../../../../../lib/format';
import type { StagedFile } from '../../../../../lib/media/pickFiles';
import { formatTimecode, frameFileStem } from '../../../../../lib/media/timecode';
import {
  FILE_PICKER_LOADING,
  FILE_PICKER_UNAVAILABLE,
  MOMENT_DROPPED,
  MOMENT_SENT,
  type FilePicker,
  type MomentSendOutcome,
  type StagedFileState,
  type StagedMoment,
  type UploadProgressSource,
} from '../../../ChatThread';
import { bakeFrame } from '../capture/bakeFrame';
import { CanvasTaintedError, captureFrame, type CapturedFrame } from '../capture/captureFrame';
import type { AiFlag } from '../lib/aiFlags';
import { annotationReducer, initialAnnotation, toStoredShapes, type AnnotationAction, type AnnotationState } from '../lib/annotationReducer';
import { buildMarkers, type Marker, type MarkerScope } from '../lib/buildMarkers';
import { isCaptureAvailable } from '../lib/captureAvailability';
import { mergeMomentSources } from '../lib/mergeMomentSources';
import type { MediaController } from '../media/useMediaController';
import { useVideoFrameRate } from '../media/useVideoFrameRate';
import { runEscapeStack, useReviewShortcuts, type EscapeHandler, type StepDirection } from '../useReviewShortcuts';
import {
  draftFor,
  encodedImageName,
  fileInFlight,
  fileRejected,
  forgetAttemptUploads,
  hasSendContent,
  isRetryOf,
  momentInFlight,
  newSendAttempt,
  newUploadLedger,
  resetUploadLedger,
  sendCallbacksFor,
  takeAttemptServerCopies,
  takeStrandedUploads,
  uploadDropped,
  uploadFailed,
  uploadRefusal,
  uploadSettled,
  uploadStarted,
  type MomentDraft,
  type SendAttempt,
  type SendContents,
  type StagedMomentState,
} from './sendDraft';
import { sendFailureMessage } from './sendFailureCopy';
import { useCapturedFrame } from './useCapturedFrame';
import { deleteServerCopies, useEagerUploads } from './useEagerUploads';
import { useObjectUrlRegistry, type ObjectUrlRegistry } from './useObjectUrlRegistry';
import { useStagedFiles } from './useStagedFiles';

const MS_PER_SECOND = 1000;
/** Frame-step fallback when the AI metadata carries no frame rate. */
export const DEFAULT_FPS = 30;
/** A step must never land exactly on the duration — `[0, duration − ε]`. */
const END_EPSILON_MS = 1;
const CAPTURE_FAILED_TOAST = "Couldn't capture the frame. Try pausing first.";
const CAPTURE_TAINTED_TOAST = "This video can't be captured here. You can still add a timestamp.";
const BAKE_FAILED_TOAST = "Couldn't prepare the frame.";
const VERSION_STALE_FRAME_TOAST = 'The video was updated, so the captured frame was removed. Your text is still here.';
const VERSION_STALE_TIMESTAMP_TOAST = 'The video was updated, so the timestamp was removed. Your text is still here.';
const VERSION_STALE_KIND: SendFailureKind = 'version_stale';
const FILE_UNAVAILABLE_KIND: SendFailureKind = 'file_unavailable';
const NOTE_MARKER_KIND = 'note';
const CONTENT_THREAD_TYPE = 'content';
/** Markers are for the notes player alone; one stable empty list everywhere else. */
const NO_MARKERS: Marker[] = [];
const FIRST_FOCUS_NONCE = 1;

/**
 * No client-side size limit while the file policy is unknown (still loading,
 * or it couldn't be loaded): capture and sending keep working, and the server
 * still checks every file when the message is sent.
 */
const UNCHECKED_BYTES = Number.POSITIVE_INFINITY;

/** Built from the policy's frame limit, so the copy names the size the check enforces. */
function frameTooLargeToast(maxFrameBytes: number): string {
  return `This frame is too large to attach (over ${formatBytes(maxFrameBytes)}). Try capturing again.`;
}

export const UNSAVED_FRAME = 'frame';
export const UNSAVED_FILES = 'files';
export const UNSAVED_FRAME_AND_FILES = 'frame_and_files';

/**
 * What closing now would lose: the frame (a capture being marked up, or a
 * staged frame), the staged files, or both. A staged timestamp is not work.
 */
export type UnsavedWork = typeof UNSAVED_FRAME | typeof UNSAVED_FILES | typeof UNSAVED_FRAME_AND_FILES;

export interface NotesSessionHandle {
  /** True when closing now would lose an open capture, a staged frame or staged files. */
  hasUnsavedWork(): boolean;
}

/** Asks the chat to scroll to a message; a new nonce re-scrolls to the same id. */
export interface FocusMessageRequest {
  id: string;
  nonce: number;
}

export interface UseNotesSessionArgs {
  /** False on every surface that is not in anchored-notes mode — the hook goes inert. */
  enabled: boolean;
  entryId: string;
  campaignId?: string;
  versionId: string;
  canWrite: boolean;
  locked: boolean;
  controller: MediaController;
  canCapture: boolean;
  onSeekFlash: () => void;
  aiFlags: AiFlag[];
  /** The AI metadata's frame rate — used until the player has measured the video's own. */
  fps?: number;
  /** The chat's entries — with the anchored-message set, their media anchors become the markers (current version only). */
  entries: ReviewThreadEntry[];
  /** Makes the conversation visible before a note marker scrolls the chat to its message, or an attached frame lands in its composer. */
  onRevealConversation?: () => void;
  /** Imperative unsaved-work probe for callers that cannot re-render on it. */
  handleRef?: MutableRefObject<NotesSessionHandle | null>;
  /** A dialog over the panel (the close prompt) owns the keyboard: the review shortcuts stand down. */
  suspendShortcuts?: boolean;
}

export interface NotesSession {
  enabled: boolean;
  /** The roster entry whose thread this is — what a marker's frame thumbnail is fetched under. */
  entryId: string;
  canWrite: boolean;
  /** The pixels are readable at all (CORS, and an untainted canvas). */
  canCapture: boolean;
  /** Capture is possible *right now* — the one gate every entry point reads. */
  canCaptureNow: boolean;
  isLocked: boolean;
  controller: MediaController;
  fps: number;
  markers: Marker[];

  /** The chat's input — focused after staging a moment. */
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  staged: StagedMoment | null;
  /** Pins the playhead as it is now; a no-op while a frame is staged (its time wins). */
  addTimestamp(): void;
  clearStaged(): void;
  /** Files waiting to ride the next send, with their chips' ring and failed marks. */
  files: StagedFileState[];
  /** The chips' live upload progress — read by each chip, so a tick re-renders nothing else. */
  uploadProgress: UploadProgressSource;
  /**
   * The Attach button's picker: ready once the server's file policy has
   * loaded — it stages the picks that pass the policy's checks and toasts one
   * refusal per file that doesn't.
   */
  filePicker: FilePicker;
  /** × on a file chip: unstages it and releases its preview, and stops its upload — or deletes the server's copy — unless a send in flight carries it. */
  removeFile(id: string): void;
  /**
   * Sends `body` with what's staged: the moment (never on a reply) and the
   * files, as a reply to `replyTo` when set. Images are compressed first.
   * Clears only what it sent, and only on success. Resolves `dropped` when
   * the moment's cut is gone (moment cleared + toasted; files and text stay)
   * or nothing is staged; rejects on any other failure — its files marked
   * failed — so the chat restores the text and shows its failure banner.
   */
  sendRich(body: string, replyTo?: string): Promise<MomentSendOutcome>;
  /** The chat's file viewer opened or closed: the review shortcuts stand down while it's open, so its keys stay its own. */
  setFileViewerOpen(open: boolean): void;
  /** "Note at": seek, stage that timestamp and focus the input. */
  pinToTime(tMs: number): void;
  seekTo(tMs: number): void;
  /** A scrubber marker was clicked: seek, and for a chat moment reveal the conversation and scroll to it. */
  selectMarker(marker: Marker): void;
  frameStep(direction: StepDirection): void;
  focusMessage: FocusMessageRequest | null;

  capture: CapturedFrame | null;
  annotation: AnnotationState;
  dispatchAnnotation: Dispatch<AnnotationAction>;
  baking: boolean;
  requestCapture(): void;
  attachFrame(): Promise<void>;
  discardCapture(): void;

  replaceConfirm: boolean;
  setReplaceConfirm(open: boolean): void;
  confirmReplace(): void;

  paletteOpen: boolean;
  setPaletteOpen(open: boolean): void;
  fullView: boolean;
  setFullView(next: boolean | ((prev: boolean) => boolean)): void;

  runEscape(): boolean;
  hasUnsavedWork(): boolean;
  /** What closing now would lose, or null when nothing would be — so a close prompt can say which. */
  unsavedWork(): UnsavedWork | null;
}

function noop(): void {
  // Capture is unavailable right now — see `isCaptureAvailable`.
}

/** The AI metadata's frame rate when it has one, else the design's fallback. */
function resolveFps(fps: number | undefined): number {
  if (fps === undefined || !Number.isFinite(fps) || fps <= 0) return DEFAULT_FPS;
  return fps;
}

function toMomentMs(tMs: number): number {
  return Math.max(0, Math.round(tMs));
}

/** A moment's payload, built once when it's staged. */
function buildMoment(tMs: number, frame?: FrameDraft): MomentDraft {
  return { tMs, frame };
}

/**
 * Upload name for a staged frame — `frame-M-SS.webp` (`.jpg` for a JPEG
 * bake): the frame's stem plus the extension of the bytes the bake produced.
 */
function frameFileName(tMs: number, contentType: EncodedImage['contentType']): string {
  return encodedImageName(frameFileStem(tMs), contentType);
}

function frameKeepsItsTimeToast(tMs: number): string {
  return `The attached frame keeps its own time. Remove it to note ${formatTimecode(tMs)} instead.`;
}

function hasStagedFrame(state: StagedMomentState | null): boolean {
  return !!state?.moment.frame;
}

function stagedFrameHasMarks(state: StagedMomentState | null): boolean {
  return (state?.moment.frame?.shapes.length ?? 0) > 0;
}

function hasStagedFiles(files: StagedFileState[]): boolean {
  return files.length > 0;
}

/** The attempt's files the composer no longer stages — removed while the send was in flight. */
function filesNotIn(attempted: StagedFile[], staged: StagedFileState[]): StagedFile[] {
  function isUnstaged(file: StagedFile): boolean {
    return !staged.some((stagedFile) => stagedFile.id === file.id);
  }
  return attempted.filter(isUnstaged);
}

interface WorkInProgress {
  /** A capture being marked up, or a staged frame. */
  frame: boolean;
  files: boolean;
}

function unsavedWorkOf({ frame, files }: WorkInProgress): UnsavedWork | null {
  if (frame && files) return UNSAVED_FRAME_AND_FILES;
  if (frame) return UNSAVED_FRAME;
  if (files) return UNSAVED_FILES;
  return null;
}

function toStagedMoment(state: StagedMomentState | null): StagedMoment | null {
  if (!state) return null;
  if (!state.frameUrl) return { tMs: state.moment.tMs };
  return { tMs: state.moment.tMs, frameUrl: state.frameUrl };
}

/** Replies use the original message's moment, so only a new message carries the staged one. */
function momentForSend(staged: StagedMomentState | null, replyTo?: string): StagedMomentState | null {
  if (replyTo) return null;
  return staged;
}

/** The attempt to send `contents` with: the last one again when this is its retry, else a new one. */
function attemptFor(lastAttemptRef: MutableRefObject<SendAttempt | null>, contents: SendContents): SendAttempt {
  const last = lastAttemptRef.current;
  if (last && isRetryOf(last, contents)) return last;
  const attempt = newSendAttempt(contents);
  lastAttemptRef.current = attempt;
  return attempt;
}

/** A settled attempt is never retried: the next send starts a new one. */
function forgetAttempt(lastAttemptRef: MutableRefObject<SendAttempt | null>, attempt: SendAttempt): void {
  if (lastAttemptRef.current !== attempt) return;
  lastAttemptRef.current = null;
}

/** Why the moment vanished when its cut went away — a frame is unsaved work, a timestamp is not. */
function versionStaleToast(state: StagedMomentState): string {
  if (hasStagedFrame(state)) return VERSION_STALE_FRAME_TOAST;
  return VERSION_STALE_TIMESTAMP_TOAST;
}

function isFailureOfKind(err: unknown, kind: SendFailureKind): err is MessageSendError {
  return err instanceof MessageSendError && err.kind === kind;
}

/** Says why a send failed when the server said — a refused file or frame, or what to wait for; the chat's banner covers the rest. */
function toastSendFailure(err: unknown): void {
  if (!(err instanceof MessageSendError)) return;
  const message = sendFailureMessage(err);
  if (!message) return;
  toast.error(message);
}

/** Stages the files that pass `policy`'s checks, and starts uploading each at once. */
type PickAndUpload = (picked: File[], policy: ThreadFilePolicy) => void;

/** The Attach button's picker once the policy is known: the system picker narrowed to its types, and picks checked against it. */
function readyFilePicker(policy: ThreadFilePolicy, pickAndUpload: PickAndUpload): FilePicker {
  function pickWithPolicy(picked: File[]): void {
    pickAndUpload(picked, policy);
  }
  return { status: 'ready', accept: threadFileAccept(policy), onPick: pickWithPolicy };
}

/** Ready as soon as a policy is in hand (a failed refetch keeps the last one); off while it loads, or once it has failed. */
function filePickerFor(policy: ThreadFilePolicy | undefined, failed: boolean, pickAndUpload: PickAndUpload): FilePicker {
  if (policy) return readyFilePicker(policy, pickAndUpload);
  if (failed) return FILE_PICKER_UNAVAILABLE;
  return FILE_PICKER_LOADING;
}

/** The limit an uploaded file must fit — unchecked while the policy is unknown. */
function fileByteLimit(policy: ThreadFilePolicy | undefined): number {
  return policy?.max_file_bytes ?? UNCHECKED_BYTES;
}

/** The limit a captured frame is baked within — unchecked while the policy is unknown. */
function frameByteLimit(policy: ThreadFilePolicy | undefined): number {
  return policy?.max_frame_bytes ?? UNCHECKED_BYTES;
}

/** Only the notes player shows markers, so every other surface skips building them. */
function markersFor(enabled: boolean, entries: ReviewThreadEntry[], aiFlags: AiFlag[], scope: MarkerScope): Marker[] {
  if (!enabled) return NO_MARKERS;
  return buildMarkers(entries, aiFlags, scope);
}

interface KeyboardOwners {
  suspendShortcuts: boolean;
  replaceConfirm: boolean;
  fileViewerOpen: boolean;
}

/**
 * The shortcuts listen on the whole document, so while a dialog is up — the
 * close prompt, the replace-frame confirm or the chat's file viewer — its
 * keys (Esc, Space, the arrows) are its own.
 */
function dialogOwnsKeyboard({ suspendShortcuts, replaceConfirm, fileViewerOpen }: KeyboardOwners): boolean {
  return suspendShortcuts || replaceConfirm || fileViewerOpen;
}

/**
 * The playhead right now. `currentTimeMs` is the last `timeupdate` (up to
 * ~250 ms behind a playing video); the element's own clock is exact.
 */
function livePlayheadMs(controller: MediaController): number {
  const element = controller.element;
  if (!element) return controller.currentTimeMs;
  return element.currentTime * MS_PER_SECOND;
}

function nextFocusRequest(id: string) {
  return function advance(prev: FocusMessageRequest | null): FocusMessageRequest {
    const nonce = prev ? prev.nonce + 1 : FIRST_FOCUS_NONCE;
    return { id, nonce };
  };
}

interface StagedMomentStore {
  stagedState: StagedMomentState | null;
  stagedRef: MutableRefObject<StagedMomentState | null>;
  /** Replaces the staged moment; the old preview is revoked unless a send showing it is in flight. */
  replaceStaged(next: StagedMomentState | null): void;
  /** Empties the composer after a successful send, keeping the preview alive for the sent bubble. */
  releaseStaged(): void;
}

/**
 * Object-URL lifecycle: a staged frame's url is revoked when the user removes
 * or replaces it, and on unmount (`urls`) — never when its send succeeds,
 * because the just-sent bubble is still painting it.
 */
function useStagedMomentStore(urls: ObjectUrlRegistry, sendingRef: MutableRefObject<Set<SendAttempt>>): StagedMomentStore {
  const [stagedState, setStagedState] = useState<StagedMomentState | null>(null);
  const stagedRef = useRef<StagedMomentState | null>(null);

  const replaceStaged = useCallback(
    (next: StagedMomentState | null) => {
      const previous = stagedRef.current;
      stagedRef.current = next;
      setStagedState(next);
      if (!previous || previous === next) return;
      if (momentInFlight(sendingRef.current, previous)) return;
      urls.revoke(previous.frameUrl);
    },
    [urls, sendingRef],
  );

  const releaseStaged = useCallback(() => {
    stagedRef.current = null;
    setStagedState(null);
  }, []);

  return { stagedState, stagedRef, replaceStaged, releaseStaged };
}

export function useNotesSession(args: UseNotesSessionArgs): NotesSession {
  const { enabled, entryId, campaignId, versionId, canWrite, locked, controller, onSeekFlash, aiFlags, entries, handleRef } = args;
  const { onRevealConversation, suspendShortcuts = false } = args;
  const measuredFps = useVideoFrameRate(controller.element, enabled);
  const fps = resolveFps(measuredFps ?? args.fps);

  const send = useSendMessage({ entryId, campaignId, versionId });
  const urls = useObjectUrlRegistry();
  // Sends in flight — what their optimistic bubbles show keeps its preview url.
  const sendingRef = useRef(new Set<SendAttempt>());
  // The latest send, until it succeeds — a retry of it reuses its draft.
  const lastAttemptRef = useRef<SendAttempt | null>(null);
  // What the server already holds from this session's sends — no staged item uploads twice.
  const [ledger] = useState(newUploadLedger);
  const { stagedState, stagedRef, replaceStaged, releaseStaged } = useStagedMomentStore(urls, sendingRef);
  const {
    files,
    filesRef,
    pickFiles,
    removeFile: unstageFile,
    clearFiles,
    updateFiles,
    uploadProgress,
    showProgress: showFileProgress,
    releaseFiles,
    releaseRemovedPreviews,
  } = useStagedFiles(urls, sendingRef);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [annotation, dispatchAnnotation] = useReducer(annotationReducer, undefined, initialAnnotation);
  const [capture, setCapture] = useCapturedFrame();
  // The open capture as of the last commit, so async work can tell whether its capture is still the one open.
  const captureRef = useRef(capture);
  useLayoutEffect(() => {
    captureRef.current = capture;
  }, [capture]);
  const [replaceConfirm, setReplaceConfirm] = useState(false);
  // The source (entry + cut) whose pixels turned out unreadable at capture time: capture stays off for it.
  const [taintedSource, setTaintedSource] = useState<string | null>(null);
  const sourceKey = `${entryId}:${versionId}`;
  const [baking, setBaking] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [fullView, setFullView] = useState(false);
  const [focusMessage, setFocusMessage] = useState<FocusMessageRequest | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isLocked = locked;
  const composerDisabled = isLocked || !canWrite;

  // Only a writer on an open thread attaches files or frames, so only they need the policy.
  const needsFilePolicy = enabled && !composerDisabled;
  const filePolicyQuery = useThreadFilePolicy(entryId, { enabled: needsFilePolicy });
  const filePolicy = filePolicyQuery.data;
  const filePolicyFailed = filePolicyQuery.isError;

  /** The server refused a file as it uploaded, before any send: its chip shows failed and the user is told why. */
  const markRejected = useCallback(
    (id: string, err: unknown) => {
      const rejected = filesRef.current.find((file) => file.id === id);
      if (!rejected) return;
      updateFiles(fileRejected(id));
      toastSendFailure(uploadRefusal(err, rejected.name));
    },
    [filesRef, updateFiles],
  );

  /** An eager upload ended: its chip drops the ring until a send takes the file on. */
  const showFileSettled = useCallback((id: string) => updateFiles(uploadSettled(id)), [updateFiles]);

  const eager = useEagerUploads({
    entryId,
    ledger,
    maxFileBytes: fileByteLimit(filePolicy),
    onProgress: showFileProgress,
    onRejected: markRejected,
    onSettled: showFileSettled,
    inFlightRef: sendingRef,
  });

  const pickAndUpload = useCallback(
    (picked: File[], policy: ThreadFilePolicy) => pickFiles(picked, policy).forEach(eager.startFile),
    [pickFiles, eager],
  );
  const filePicker = useMemo(() => filePickerFor(filePolicy, filePolicyFailed, pickAndUpload), [filePolicy, filePolicyFailed, pickAndUpload]);

  /** Swaps the staged moment: a new frame uploads at once, and the frame it replaces is deleted unless a send in flight carries it. */
  const swapStaged = useCallback(
    (next: StagedMomentState | null) => {
      const previous = stagedRef.current;
      replaceStaged(next);
      if (previous && previous !== next && !momentInFlight(sendingRef.current, previous)) eager.discardFrame(previous);
      if (next && next !== previous) eager.startFrame(next);
    },
    [stagedRef, replaceStaged, eager],
  );

  const focusInput = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  // Callbacks that only read the controller when invoked go through this ref,
  // so they (and the chat's composer built from them) keep one identity
  // whatever the controller does during playback. Written in a layout effect,
  // never during render.
  const controllerRef = useRef(controller);
  useLayoutEffect(() => {
    controllerRef.current = controller;
  }, [controller]);

  // Set once the session unmounts, for a send still in flight to tell its session is gone.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const seenVersionRef = useRef(versionId);
  const seenEntryRef = useRef(entryId);

  // Leaving an entry (another entry in the same mounted panel, or unmounting): uploads that finished but no
  // send will carry are deleted under that entry, so they stop holding the user's unsent-upload quota. A send
  // still in flight keeps its own — it is posting them.
  useEffect(() => {
    const inFlight = sendingRef.current;
    return () => deleteServerCopies(entryId, resetUploadLedger(ledger, inFlight));
  }, [entryId, ledger]);

  // Another entry in the same mounted panel (the public shell doesn't key it per entry): what is staged belongs
  // to the old entry, whose file ids the new one doesn't know. In-transfer uploads stop, except those a send in
  // flight carries; the staged items go without deleting anything here (the effect above deleted the old
  // entry's uploads). Declared before the version effect so a switch that also changes the version finds
  // nothing left to drop.
  useEffect(() => {
    if (seenEntryRef.current === entryId) return;
    seenEntryRef.current = entryId;
    eager.abortAll(sendingRef.current);
    clearFiles();
    replaceStaged(null);
    lastAttemptRef.current = null;
  }, [entryId, eager, clearFiles, replaceStaged]);

  // A new content version invalidates the staged moment and any open capture — they belong to the old cut.
  useEffect(() => {
    if (seenVersionRef.current === versionId) return;
    seenVersionRef.current = versionId;
    const dropped = stagedRef.current;
    swapStaged(null);
    setCapture(null);
    setReplaceConfirm(false);
    dispatchAnnotation({ type: 'clear' });
    if (!dropped) return;
    toast.error(versionStaleToast(dropped));
  }, [versionId, stagedRef, swapStaged, setCapture]);

  // Every page of the thread's notes for this cut; an older backend (or a failed fetch) leaves the chat's page alone.
  const anchoredQuery = useAnchoredMessages(entryId, CONTENT_THREAD_TYPE, versionId, { enabled });
  const anchoredMessages = anchoredQuery.data;
  const momentEntries = useMemo(() => mergeMomentSources(entries, anchoredMessages), [entries, anchoredMessages]);
  const markers = useMemo(
    () => markersFor(enabled, momentEntries, aiFlags, { versionId, durationMs: controller.durationMs }),
    [enabled, momentEntries, aiFlags, versionId, controller.durationMs],
  );

  const seekTo = useCallback(
    (tMs: number) => {
      void controller.seek(tMs, { pause: true });
      onSeekFlash();
    },
    [controller, onSeekFlash],
  );

  const selectMarker = useCallback(
    (marker: Marker) => {
      seekTo(marker.tMs);
      if (marker.kind !== NOTE_MARKER_KIND) return;
      // The conversation may be collapsed — open it first, or the scroll lands in a hidden column.
      onRevealConversation?.();
      setFocusMessage(nextFocusRequest(marker.id));
    },
    [seekTo, onRevealConversation],
  );

  /**
   * True from the moment a step asks for a seek until the browser settles it.
   * The guard lives here rather than in the transport because all three
   * callers — the buttons, the scrubber's arrow keys and the global ← / →
   * shortcut — come through this one function, and a held arrow key repeats
   * far faster than a decoder seek completes.
   */
  const stepInFlightRef = useRef(false);

  const frameStep = useCallback(
    (direction: StepDirection) => {
      const stepMs = MS_PER_SECOND / fps;
      const lastMs = Math.max(0, controller.durationMs - END_EPSILON_MS);
      // The element's own clock: `currentTimeMs` lags a playing video by up to a `timeupdate`.
      const playheadMs = livePlayheadMs(controller);
      const target = Math.min(lastMs, Math.max(0, playheadMs + direction * stepMs));
      // A step that is already clamped against an end of the clip cannot move
      // the playhead, so the browser fires no `seeked`. Arming the latch for it
      // would swallow the *next* step — the one going back the other way.
      const moves = target !== playheadMs;

      if (moves) {
        if (stepInFlightRef.current) return;
        stepInFlightRef.current = true;
      }

      // No seek flash here: stepping is held down, and a flash per frame strobes.
      const settled = controller.seek(target, { pause: true });
      if (!moves) return;
      // `controller.seek` always settles — on `seeked` or on its own 2 s
      // timeout — so the latch is bounded even if the browser clamps the seek.
      void settled.then(
        () => {
          stepInFlightRef.current = false;
        },
        () => {
          stepInFlightRef.current = false;
        },
      );
    },
    [controller, fps],
  );

  const stageTimestamp = useCallback(
    (tMs: number) => {
      // A staged frame carries its own time — a timestamp never replaces it.
      if (hasStagedFrame(stagedRef.current)) return;
      replaceStaged({ moment: buildMoment(toMomentMs(tMs)) });
    },
    [stagedRef, replaceStaged],
  );

  const addTimestamp = useCallback(() => {
    stageTimestamp(livePlayheadMs(controllerRef.current));
    focusInput();
  }, [stageTimestamp, focusInput]);

  const pinToTime = useCallback(
    (tMs: number) => {
      seekTo(tMs);
      if (hasStagedFrame(stagedRef.current)) toast.info(frameKeepsItsTimeToast(tMs));
      stageTimestamp(tMs);
      focusInput();
    },
    [seekTo, stagedRef, stageTimestamp, focusInput],
  );

  const clearStaged = useCallback(() => swapStaged(null), [swapStaged]);

  /**
   * × on a chip: the file goes. A send in flight carries it and claims its
   * upload (or, if that send doesn't go out, releases it — see
   * `releaseWithdrawn`), so its upload is left alone; an upload nothing
   * sends is stopped, or its server copy deleted.
   */
  const removeFile = useCallback(
    (id: string) => {
      unstageFile(id);
      if (fileInFlight(sendingRef.current, id)) return;
      eager.discardFile(id);
    },
    [unstageFile, eager],
  );

  /**
   * A successful send empties what it carried — only that: a file staged or
   * removed meanwhile stays as it is — and keeps the preview urls alive for
   * the sent bubble. The message claimed every upload it carried.
   */
  const settleSentAttempt = useCallback(
    (attempt: SendAttempt) => {
      forgetAttempt(lastAttemptRef, attempt);
      forgetAttemptUploads(ledger, attempt);
      releaseFiles(attempt.files);
      if (!attempt.staged || stagedRef.current !== attempt.staged) return;
      releaseStaged();
    },
    [ledger, releaseFiles, stagedRef, releaseStaged],
  );

  /**
   * A send that didn't go out takes its optimistic bubble down, so what only
   * that bubble still held — the previews of a frame or files removed while
   * it was in flight, and their uploads, which nothing will send now — is
   * released now rather than on unmount.
   */
  const releaseWithdrawn = useCallback(
    (attempt: SendAttempt) => {
      // A send that outlived its session and failed has no retry: its uploads were spared at unmount, so delete them here.
      if (unmountedRef.current) deleteServerCopies(entryId, takeAttemptServerCopies(ledger, attempt));
      function discardUpload(file: StagedFile): void {
        eager.discardFile(file.id);
      }
      releaseRemovedPreviews(attempt.files);
      filesNotIn(attempt.files, filesRef.current).forEach(discardUpload);
      if (!attempt.staged || stagedRef.current === attempt.staged) return;
      urls.revoke(attempt.staged.frameUrl);
      eager.discardFrame(attempt.staged);
    },
    [releaseRemovedPreviews, eager, filesRef, stagedRef, urls, entryId, ledger],
  );

  /**
   * The cut the moment points at is gone: drop the moment and say so — the
   * chat keeps the text, the composer the files, and the next send reuses
   * their uploads.
   */
  const dropStaleMoment = useCallback(
    (attempt: SendAttempt, sent: StagedMomentState) => {
      forgetAttempt(lastAttemptRef, attempt);
      updateFiles(uploadDropped(attempt));
      releaseWithdrawn(attempt);
      if (stagedRef.current === sent) swapStaged(null);
      toast.error(versionStaleToast(sent));
    },
    [updateFiles, releaseWithdrawn, stagedRef, swapStaged],
  );

  /**
   * A failed send keeps what it carried for a retry, marking each file the
   * server doesn't hold. Dead uploads end the attempt and leave the ledger,
   * so the retry uploads them again under a new client id; a refused file or
   * frame is named, with why, and a throttled send says what to do.
   */
  const failAttempt = useCallback(
    (attempt: SendAttempt, err: unknown) => {
      updateFiles(uploadFailed(attempt, ledger));
      releaseWithdrawn(attempt);
      if (isFailureOfKind(err, FILE_UNAVAILABLE_KIND)) {
        forgetAttempt(lastAttemptRef, attempt);
        forgetAttemptUploads(ledger, attempt);
      }
      toastSendFailure(err);
    },
    [updateFiles, ledger, releaseWithdrawn],
  );

  const sendRich = useCallback(
    async (body: string, replyTo?: string): Promise<MomentSendOutcome> => {
      const contents: SendContents = { body, staged: momentForSend(stagedRef.current, replyTo), files: filesRef.current, replyTo };
      // Nothing to send with the text — the chat gives it back, no failure banner.
      if (!hasSendContent(contents)) return MOMENT_DROPPED;
      // A retry sends the SAME draft object — the sender's idempotency key.
      const attempt = attemptFor(lastAttemptRef, contents);
      sendingRef.current.add(attempt);
      // Files picked while this send uploads wait, so the two never exceed the server's per-user upload limit.
      const releaseOtherUploads = eager.holdOthers(attempt);
      try {
        // What attaching already started finishes first: the draft then names those uploads, and the chips' rings start where they stand.
        await eager.settled(attempt);
        updateFiles(uploadStarted(attempt, ledger));
        const draft = await draftFor(attempt, ledger, fileByteLimit(filePolicy));
        await send(body, draft, sendCallbacksFor(attempt, ledger, showFileProgress));
      } catch (err) {
        sendingRef.current.delete(attempt);
        const sentMoment = attempt.staged;
        // The cut the moment points at is gone: not a failure — the chat gives the text back.
        if (sentMoment && isFailureOfKind(err, VERSION_STALE_KIND)) {
          dropStaleMoment(attempt, sentMoment);
          return MOMENT_DROPPED;
        }
        failAttempt(attempt, err);
        throw err;
      } finally {
        releaseOtherUploads();
      }
      sendingRef.current.delete(attempt);
      settleSentAttempt(attempt);
      return MOMENT_SENT;
    },
    [stagedRef, filesRef, eager, updateFiles, ledger, filePolicy, send, showFileProgress, dropStaleMoment, failAttempt, settleSentAttempt],
  );

  const startCapture = useCallback(async () => {
    try {
      const frame = await captureFrame(controllerRef.current);
      dispatchAnnotation({ type: 'clear' });
      setCapture(frame);
    } catch (err) {
      console.error('[useNotesSession] capturing the frame failed', err);
      if (err instanceof CanvasTaintedError) {
        // This source can never be read; every capture entry point stands down for it.
        setTaintedSource(sourceKey);
        toast.error(CAPTURE_TAINTED_TOAST);
        return;
      }
      toast.error(CAPTURE_FAILED_TOAST);
    }
  }, [setCapture, sourceKey]);

  const requestCapture = useCallback(() => {
    // Capture from full view closes the modal first, then annotates in place.
    setFullView(false);
    setPaletteOpen(false);
    if (stagedFrameHasMarks(stagedState)) {
      setReplaceConfirm(true);
      return;
    }
    void startCapture();
  }, [stagedState, startCapture]);

  const confirmReplace = useCallback(() => {
    setReplaceConfirm(false);
    void startCapture();
  }, [startCapture]);

  const attachFrame = useCallback(async () => {
    // Re-entrant: the bar's Attach is only disabled once `baking` has rendered,
    // so a double click (or Attach racing the C shortcut) can arrive twice.
    if (!capture || baking) return;
    setBaking(true);
    const maxFrameBytes = frameByteLimit(filePolicy);
    // A new cut while baking has already dropped this capture (and closed its bitmap).
    const bakedForVersion = seenVersionRef.current;
    const cutChanged = (): boolean => seenVersionRef.current !== bakedForVersion;
    // Escape (or a new capture) while baking closed this capture: what was baked is no longer wanted.
    const captureClosed = (): boolean => captureRef.current !== capture;
    try {
      const baked = await bakeFrame(capture.bitmap, annotation.shapes, maxFrameBytes);
      // Unmounted while baking: the url cleanup has already run, so never mint one now.
      if (!urls.isMounted() || cutChanged() || captureClosed()) return;
      // Over the frame limit even at the lowest quality: never staged, and the capture stays open.
      if (!baked) {
        toast.error(frameTooLargeToast(maxFrameBytes));
        return;
      }
      const frame: FrameDraft = {
        image: baked,
        shapes: toStoredShapes(annotation.shapes),
        name: frameFileName(capture.tMs, baked.contentType),
      };
      swapStaged({ moment: buildMoment(capture.tMs, frame), frameUrl: urls.create(baked.blob) });
      setCapture(null);
      dispatchAnnotation({ type: 'clear' });
      // The transport's Capture and the C shortcut work with the conversation collapsed: open it, or the frame lands in a hidden composer.
      onRevealConversation?.();
      focusInput();
    } catch (err) {
      console.error('[useNotesSession] preparing the captured frame failed', err);
      if (cutChanged()) return;
      toast.error(BAKE_FAILED_TOAST);
    } finally {
      setBaking(false);
    }
  }, [capture, baking, filePolicy, annotation.shapes, urls, swapStaged, setCapture, onRevealConversation, focusInput]);

  const discardCapture = useCallback(() => {
    setCapture(null);
    setPaletteOpen(false);
    dispatchAnnotation({ type: 'clear' });
  }, [setCapture]);

  // Escape precedence: palette → full view → capture (the chat handles its own file viewer and reply).
  const escapeStack = useMemo<EscapeHandler[]>(() => {
    function closePaletteOnEscape(): boolean {
      if (!paletteOpen) return false;
      setPaletteOpen(false);
      return true;
    }
    function closeFullViewOnEscape(): boolean {
      if (!fullView) return false;
      setFullView(false);
      return true;
    }
    function discardCaptureOnEscape(): boolean {
      if (!capture) return false;
      discardCapture();
      return true;
    }
    return [closePaletteOnEscape, closeFullViewOnEscape, discardCaptureOnEscape];
  }, [paletteOpen, fullView, capture, discardCapture]);

  const runEscape = useCallback(() => runEscapeStack(escapeStack), [escapeStack]);

  const unsavedWork = useCallback(
    () => unsavedWorkOf({ frame: !!capture || hasStagedFrame(stagedState), files: hasStagedFiles(files) }),
    [capture, stagedState, files],
  );

  const hasUnsavedWork = useCallback(() => unsavedWork() !== null, [unsavedWork]);

  useImperativeHandle(handleRef, () => ({ hasUnsavedWork }), [hasUnsavedWork]);

  function guardUnload(event: BeforeUnloadEvent): void {
    if (!hasUnsavedWork()) return;
    event.preventDefault();
  }

  useWindowEvent('beforeunload', guardUnload, { enabled });

  // A reload or close with uploaded-but-unsent files would hold the thread's pending quota for 24 h: free them now.
  // A send in flight keeps its own uploads.
  function freeStrandedUploads(): void {
    deleteThreadFilesOnExit(entryId, takeStrandedUploads(ledger, sendingRef.current, stagedRef.current));
  }

  useWindowEvent('pagehide', freeStrandedUploads, { enabled });

  // The page came back from the back/forward cache: the copies freed on pagehide are gone, so what is still staged uploads again.
  function restoreStrandedUploads(event: PageTransitionEvent): void {
    if (!event.persisted) return;
    const carried = new Set([...sendingRef.current].flatMap((attempt) => attempt.files.map((file) => file.id)));
    filesRef.current.forEach((file) => {
      if (!file.failed && !carried.has(file.id)) eager.startFile(file);
    });
    const staged = stagedRef.current;
    if (staged && !momentInFlight(sendingRef.current, staged)) eager.startFrame(staged);
  }

  useWindowEvent('pageshow', restoreStrandedUploads, { enabled });

  const canCapture = args.canCapture && taintedSource !== sourceKey;
  const canCaptureNow = isCaptureAvailable({
    enabled,
    canCapture,
    ready: controller.ready,
    composerDisabled,
    annotating: !!capture,
  });
  const captureShortcut = canCaptureNow ? requestCapture : noop;
  const toggleFullView = useCallback(() => setFullView((prev) => !prev), []);
  // Full view covers the markup bar, and requestCapture already leaves it.
  const fullViewShortcut = capture ? noop : toggleFullView;
  const shortcutsEnabled = enabled && !dialogOwnsKeyboard({ suspendShortcuts, replaceConfirm, fileViewerOpen });

  useReviewShortcuts({
    enabled: shortcutsEnabled,
    controller,
    onCapture: captureShortcut,
    onStep: frameStep,
    onFullView: fullViewShortcut,
    transportFrozen: !!capture,
    escapeStack,
  });

  const staged = useMemo(() => toStagedMoment(stagedState), [stagedState]);

  // One identity until something in it changes, so a consumer's `useCallback(…, [session])` and a
  // memoised child holding it stay put while the panel re-renders for anything else.
  return useMemo<NotesSession>(
    () => ({
      enabled,
      entryId,
      canWrite,
      canCapture,
      canCaptureNow,
      isLocked,
      controller,
      fps,
      markers,

      textareaRef,
      staged,
      addTimestamp,
      clearStaged,
      files,
      uploadProgress,
      filePicker,
      removeFile,
      sendRich,
      setFileViewerOpen,
      pinToTime,
      seekTo,
      selectMarker,
      frameStep,
      focusMessage,

      capture,
      annotation,
      dispatchAnnotation,
      baking,
      requestCapture,
      attachFrame,
      discardCapture,

      replaceConfirm,
      setReplaceConfirm,
      confirmReplace,

      paletteOpen,
      setPaletteOpen,
      fullView,
      setFullView,

      runEscape,
      hasUnsavedWork,
      unsavedWork,
    }),
    [
      enabled,
      entryId,
      canWrite,
      canCapture,
      canCaptureNow,
      isLocked,
      controller,
      fps,
      markers,
      staged,
      addTimestamp,
      clearStaged,
      files,
      uploadProgress,
      filePicker,
      removeFile,
      sendRich,
      pinToTime,
      seekTo,
      selectMarker,
      frameStep,
      focusMessage,
      capture,
      annotation,
      baking,
      requestCapture,
      attachFrame,
      discardCapture,
      replaceConfirm,
      confirmReplace,
      paletteOpen,
      fullView,
      runEscape,
      hasUnsavedWork,
      unsavedWork,
    ],
  );
}
